import type { Holiday } from "@dutyon/apis";
import {
  createHoliday,
  deleteHoliday,
  formatDutyDateKey,
  getHolidays,
  queryKeys,
  updateHoliday,
} from "@dutyon/apis";
import {
  Button,
  Card,
  Group,
  Loader,
  LoadingOverlay,
  Stack,
  Table,
  Text,
  TitleContainer,
  useModal,
} from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HolidayForm } from "./components/HolidayForm";

export function HolidayListPage() {
  const queryClient = useQueryClient();

  const {
    data: holidays = [],
    isLoading,
    isFetching,
  } = useQuery<Holiday[]>({
    queryKey: queryKeys.holidays.all,
    queryFn: async () => {
      const response = await getHolidays({});
      return response.list;
    },
  });

  const holidayModal = useModal();

  const createMutation = useMutation({
    mutationFn: (data: { name: string; date: string }) => createHoliday(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.holidays.all });
      holidayModal.close("holiday-add-modal");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; data: { name?: string; date?: string } }) =>
      updateHoliday(vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.holidays.all });
      holidayModal.close("holiday-add-modal");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (vars: { id: string }) => deleteHoliday(vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.holidays.all });
    },
  });

  const openHolidayModal = (holiday?: Holiday) => {
    const initValues = holiday
      ? {
          name: holiday.name,
          date: new Date(`${formatDutyDateKey(holiday.date)}T00:00:00`),
        }
      : { name: "", date: null };

    const currentId = holiday ? holiday.id : null;

    holidayModal.open({
      modalId: "holiday-add-modal",
      title: currentId ? "공휴일 수정" : "공휴일 추가",
      children: (
        <Stack gap="md">
          <HolidayForm
            formId="holiday-form"
            initialValues={initValues}
            onSubmit={(values) => {
              if (currentId) {
                updateMutation.mutate({
                  id: currentId,
                  data: {
                    name: values.name.trim(),
                    date: formatDutyDateKey(values.date),
                  },
                });
              } else {
                createMutation.mutate({
                  name: values.name.trim(),
                  date: formatDutyDateKey(values.date),
                });
              }
            }}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => holidayModal.close("holiday-add-modal")}>
              취소
            </Button>
            <Button
              type="submit"
              form="holiday-form"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              저장
            </Button>
          </Group>
        </Stack>
      ),
    });
  };

  const formatDate = (dateString: string | Date) => {
    return new Date(`${formatDutyDateKey(dateString)}T00:00:00`).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "short",
    });
  };

  return (
    <TitleContainer
      title="공휴일 관리"
      rightSection={<Button onClick={() => openHolidayModal()}>공휴일 추가</Button>}
    >
      <Card withBorder radius="md" p={0} pos="relative" style={{ overflow: "hidden" }}>
        <LoadingOverlay visible={isLoading || isFetching} overlayProps={{ blur: 1 }} />
        <Table.ScrollContainer minWidth={600}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>날짜</Table.Th>
                <Table.Th>이름</Table.Th>
                <Table.Th>작업</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={3} ta="center" py="xl">
                    <Stack align="center" gap="xs">
                      <Loader size="sm" />
                      <Text size="sm" c="dimmed">
                        공휴일 목록을 불러오는 중입니다...
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ) : holidays.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={3} ta="center" py="xl">
                    <Text size="sm" c="dimmed">
                      등록된 공휴일이 없습니다.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                holidays.map((holiday) => (
                  <Table.Tr key={holiday.id}>
                    <Table.Td>{formatDate(holiday.date)}</Table.Td>
                    <Table.Td>{holiday.name}</Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Button size="xs" variant="light" onClick={() => openHolidayModal(holiday)}>
                          수정
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          color="red"
                          onClick={() => {
                            if (confirm("정말 삭제하시겠습니까?")) {
                              deleteMutation.mutate({ id: holiday.id });
                            }
                          }}
                        >
                          삭제
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </TitleContainer>
  );
}
